import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import moment from 'moment'

import DateBox, { DateBoxRef } from 'devextreme-react/date-box'

import { useTranslation } from 'react-i18next';

import './index.scss'
import { FormErrors } from 'components/forms/form-errors'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'

import { useLabelWidth } from 'hooks/use-label-width'
import useResizeObserver from '@react-hook/resize-observer'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

const serializationFormats: {[k: string]: string} = {
    date: 'yyyy-MM-dd',
    gdate: 'yyyy-MM-dd',
    time: 'HH:mm',
    datetime: 'yyyy-MM-ddTHH:mm:ss.SSS'
}

const momentFormats: {[k: string]: any} = {
    date: 'YYYY-MM-DD',
    gdate: 'DD.MM.YYYY',
    time: 'HH:mm',
    datetime: ['YYYY-MM-DDTHH:mm:ss.SSS', 'YYYY-MM-DDTHH:mm:ss']
}


export const DateInput = ({label, readOnly=false, format='date', disabled=false, customValue=false, ...props}: any) => {
    const dateBoxRef = useRef<DateBoxRef>(null)
    const [{ name, onBlur, ...field }, meta] = useField(props)
    const { setFieldValue } = useFormikContext()
    const { t } = useTranslation()
    const [inFocus, setInFocus] = useState(false)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const serializationFormat = serializationFormats[format]
    const [fieldWidth, setFieldWidth] = useState<any>(null)

    const [isInvalidDate, setIsInvalidDate] = useState(false)

    const getLabelWidth = useLabelWidth(formFieldType)
    const allowedKeys = useMemo(() => ([
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter",
        '=',
        '+',
        '.',
        '/',
        '-'
    ]),[])

    const parsedValue = useMemo(() => {
        return field.value ? moment(field.value, momentFormats[format]).toISOString() : null
    }, [field.value, format])

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const logWidth = useCallback(() => {
        const dropdownBox: any = dateBoxRef.current
        const input: any = dropdownBox?.instance()?.element().querySelector('.dx-texteditor-input-container')

        input && setFieldWidth(input.getBoundingClientRect().width)
    },[])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((dateBoxRef.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        isFloatingLabel && logWidth()
    },[logWidth, parsedValue, isFloatingLabel])

    useEffect(() => {
        if (isFloatingLabel) {
            const dateBox: any = dateBoxRef.current
            const floatingLabel: HTMLElement = dateBox?.instance()?.element().querySelector('.dx-label')

            if (parsedValue) {
                dateBox?.instance()?.element()?.classList.add('input-has-value')

                floatingLabel && (floatingLabel.style.left = `calc(100% - ${readOnly ? '10px' : '60px'} - ${floatingLabel.offsetWidth-12}px)`)

                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)
                    const input: any = dateBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+26)}px`)
                }

            }
            else {
                dateBox?.instance()?.element()?.classList.remove('input-has-value')

                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)
                    const input: any = dateBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                    floatingLabel.style.left = inFocus ? `calc(100% - 40px - ${floatingLabel.offsetWidth-12}px)` : '0px'
                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+16)}px`)
                }
            }
        }

    },[parsedValue, getLabelWidth, label, fieldWidth, readOnly, inFocus, isFloatingLabel])

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const handleChange = useCallback((e: any) => {
        if (e.event === undefined) return

            let value = e.value

            if (value != null) {
                if (format === 'gdate' || format === 'date') {
                    value = moment.utc(value, momentFormats['date']).format(momentFormats[format])
                }else if (format === 'datetime'){
                    value = moment.utc(value).format(momentFormats[format][0])
                }
                else{
                    value = moment.utc(value, momentFormats[format]).format(momentFormats[format])
                }
            }

            (!value && customValue) && setIsInvalidDate(false)
            setFieldValue(name, value, true)

    }, [name, format, setFieldValue, customValue])

    const onEnter = useCallback((digitValue: any, value: any) => {
        const year = moment(digitValue, ['DD', 'DDMM', 'DDMMYY', 'DDMMYYYY']).year()

        if (digitValue && (moment(digitValue, ['DD', 'DDMM', 'DDMMYY', 'DDMMYYYY']).format(momentFormats[format]) === 'Invalid date' || year < 1900 || digitValue.length > 8)) {
            setIsInvalidDate(true)

            return
        }

        if (value === '=') {
            setFieldValue(name, moment().format(momentFormats[format]), true)
        }

        if (digitValue) {
            if (value.startsWith('+')) {
                setFieldValue(name, moment().add(Number(value.substring(1)), 'days').format(momentFormats[format]), true)
            }

            else if (value.startsWith('-')) {
                setFieldValue(name, moment().subtract(Number(value.substring(1)), 'days').format(momentFormats[format]), true)
            }

            else {
                setFieldValue(name,  moment(digitValue, ['DD', 'DDMM', 'DDMMYY', 'DDMMYYYY']).format(momentFormats[format]), true)
                setIsInvalidDate(false)
            }
        }
    },[format, name, setFieldValue])

    const handleDigitValue = useCallback((value: any) => {
        const digitValue = value.replace(/\D/g, '')

        if (digitValue.length === 6) {
                moment.parseTwoDigitYear = (yearValue: any) => {
                const year = Number(yearValue)
                return year <= 79 ? 2000 + year : 1900 + year
            }
        }

        return digitValue.length === 8 && digitValue.slice(-4).startsWith('00')
            ? digitValue.slice(0, 4) + digitValue.slice(6)
            : digitValue

    },[])

    const handleBlur = useCallback((e: any) => {
        const value = e.event.target.value
        const digitValue = handleDigitValue(value)

        customValue && onEnter(digitValue, value)
        setInFocus(false)
        onBlur(e.event)
    }, [onBlur, handleDigitValue, onEnter, customValue])

    const ddButtons = useMemo<any>(() => ([
        { name: 'clear' },
        { name: 'dropDown' },
        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }
        }
    ]), [label])

    const onKeyDown = useCallback((e: any) => {
        const value = e.event.currentTarget.value
        const key = e.event.key
        const isDigit = /^[0-9]$/.test(key)

        const digitValue = handleDigitValue(value)

        if (isDigit || allowedKeys.includes(key)) {
            if (key === 'Enter') {
                onEnter(digitValue, value)
            }
            return
        }

        e.event.preventDefault()

    },[handleDigitValue, onEnter, allowedKeys])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label htmlFor={props.id || name} className='dx-field-label'>{label}</label>
                <DateBox
                    label={label}
                    labelMode= 'floating'
                    ref={dateBoxRef}
                    type={format === 'gdate' ? 'date' : format}
                    name={name}
                    buttons={ddButtons}
                    inputAttr={{name: name}}
                    value={parsedValue}
                    onValueChanged={handleChange}
                    onFocusIn={onFocusIn}
                    onFocusOut={handleBlur}
                    className='dx-field-input'
                    disabled={disabled}
                    readOnly={readOnly}
                    dateSerializationFormat={serializationFormat}
                    acceptCustomValue={true}
                    openOnFieldClick={true}
                    {...!customValue && {
                        isValid: !(meta.touched && meta.error),
                        useMaskBehavior: true
                    }}
                    displayFormat={t('global:format.datetimeFormat', { context: format === 'gdate' ? 'date' : format })}
                    showClearButton={true}
                    height={26}

                    {...customValue && {
                        onInput: (e: any) => {!e.component.option('text') && setIsInvalidDate(false)},
                        onKeyDown: onKeyDown,
                        validationStatus: isInvalidDate ? "invalid" : 'valid',
                        validationErrors: isInvalidDate ?  [{ message: 'Value must be a date or time' }] : []
                    }}
                />
            </div>
            <FormErrors meta={meta} />
        </div>
    )
}
